import React, { Fragment } from 'react'
import { Document, Page, Text, View, Image, StyleSheet, BlobProvider } from '@react-pdf/renderer'
import { Dialog, Transition } from '@headlessui/react'
import { format } from 'date-fns'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { IoClose } from 'react-icons/io5'
import { GrDownload } from 'react-icons/gr'
import { FiPrinter } from 'react-icons/fi'

// ─── Constants ───────────────────────────────────────────────────────────────
const MAIN = '#5c2d91'
const LIGHT_BG = '#f5f5f5'
const BORDER_BLUE = '#dbeafe'
const STATIC_URL = process.env.REACT_APP_STATIC_URL || ''

// Build absolute icon URL — works for both website.icon paths and local /images/icons/* paths
// Build absolute icon URL — works for both website.icon paths and local /images/icons/* paths
const iconUrl = (path) => {
  console.log(path)
  return path
  // return `${}`
  if (!path) return null
  if (path.startsWith('http')) return path
  path = `${STATIC_URL}${path}`
  console.log(path)
  return path
}
const fmtNum = (n) => {
  if (!n || n === 0) return '-'
  return Intl.NumberFormat('en-US').format(parseInt(n)).replace(/,/g, ' ')
}

// ─── PDF Styles ──────────────────────────────────────────────────────────────
const ps = StyleSheet.create({
  page: { padding: 10, fontFamily: 'Helvetica', fontSize: 10, color: '#404040' },
  sectionBox: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    padding: 16,
    paddingBottom: 24,
  },
  sectionTitle: { fontSize: 22, fontFamily: 'Helvetica-Bold', color: MAIN, textTransform: 'uppercase' },
  sectionSubtitle: { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#000000', marginBottom: 2 },
  tableWrap: {
    backgroundColor: MAIN,
    borderRadius: 8,
    marginHorizontal: 16,
    marginTop: -12,
    paddingVertical: 12,
  },
  tableTitle: {
    color: '#ffffff',
    fontSize: 14,
    textAlign: 'center',
    paddingBottom: 8,
    paddingHorizontal: 16,
    textTransform: 'capitalize',
    fontWeight: 700,
  },
  headerRow: { flexDirection: 'row', backgroundColor: LIGHT_BG, paddingVertical: 8, paddingHorizontal: 12 },
  headerCell: { fontSize: 8, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', color: '#262626' },
  whiteHeaderRow: { flexDirection: 'row', backgroundColor: MAIN, paddingVertical: 6, paddingHorizontal: 12 },
  whiteHeaderCell: { fontSize: 8, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', color: '#ffffff' },
  subHeaderRow: { flexDirection: 'row', backgroundColor: LIGHT_BG, paddingVertical: 6, paddingHorizontal: 12 },
  subHeaderCell: { fontSize: 8, color: '#262626' },
  dataRow: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderBottom: `2px solid ${BORDER_BLUE}`,
    paddingVertical: 8,
    paddingHorizontal: 12,
    minHeight: 28,
    alignItems: 'center',
  },
  cellText: { fontSize: 10, color: '#1f2937' },
  cellBold: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#171717' },
  cellSub: { fontSize: 7, color: '#737373' },
  totalsRow: {
    flexDirection: 'row',
    backgroundColor: LIGHT_BG,
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  totalsText: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#262626', textTransform: 'uppercase' },
  accCard: { backgroundColor: '#ffffff', borderRadius: 8, marginHorizontal: 16, marginTop: 8, overflow: 'hidden' },
  accHeader: { backgroundColor: MAIN, paddingVertical: 10, paddingHorizontal: 16 },
  accHeaderText: { color: '#ffffff', fontSize: 16, fontFamily: 'Helvetica-Bold', textAlign: 'center' },
  accRow: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    borderBottom: `2px solid ${BORDER_BLUE}`,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  accRowLast: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  spacer: { marginBottom: 0 },
  socialRow: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 6, gap: 12 },
  socialItem: { fontSize: 10, color: '#000000' },
  icon: { width: 14, height: 14, marginRight: 6 },
  iconRow: { flexDirection: 'row', alignItems: 'center' },
})

// ═════════════════════════════════════════════════════════════════════════════
// REACT-PDF DOCUMENT
// ═════════════════════════════════════════════════════════════════════════════
const ReportPdfDocument = ({
  websites,
  matches,
  matchdayData,
  matchdayDataTotals,
  matchdayDataSpectators,
  highlightWebsitesTotal,
  ddlTotal,
  matchday,
  competitionName,
}) => {
  const liveRows = [...websites]
    .sort((a, b) => b.livelinks - a.livelinks)
    .filter((w) => w.name !== 'Snapchat' && w.name !== 'DDL')

  const highlightRows = [...websites].sort((a, b) => b.highlightlinks - a.highlightlinks)
  const matchTotal = [...matches].sort((a, b) => b.totallinks - a.totallinks)
  const matchLive = [...matches].sort((a, b) => b.livelinks - a.livelinks)
  const matchSpec = [...matches].sort((a, b) => b.totalspectators - a.totalspectators)
  const matchHL = [...matches].sort((a, b) => b.highlightlinks - a.highlightlinks)

  // Totals calculated using the same logic as the bein-sports Report.js HTML
  const liveTotalLinks = websites.length > 0
    ? websites.reduce((acc, v) => {
        if (v.name === 'Snapchat' || v.name === 'DDL') return acc
        return acc + (v.livelinks || 0)
      }, 0)
    : 0

  const highlightTotalLinks = websites.length > 0
    ? websites.reduce((acc, v) => acc + (v.highlightlinks || 0), 0)
    : 0

  return (
    <Document>
      <Page
        size='A4'
        orientation='landscape'
        style={ps.page}
        wrap
      >
        {/* ── PIRACY LIVE ──────────────────────────────────────── */}
        <View style={ps.spacer}>
          <View style={ps.sectionBox}>
            <Text style={ps.sectionTitle}>PIRACY Live</Text>
          </View>
          <View style={ps.tableWrap}>
            <Text style={ps.tableTitle}>Summary table for live links</Text>
            <View style={ps.headerRow}>
              <Text style={[ps.headerCell, { width: '25%', textAlign: 'left' }]}>Platforms</Text>
              <Text style={[ps.headerCell, { width: '20%', textAlign: 'center' }]}>Number of live links</Text>
              <Text style={[ps.headerCell, { width: '20%', textAlign: 'center' }]}>Average closure time</Text>
              <Text style={[ps.headerCell, { width: '15%', textAlign: 'center' }]}>Closure rate</Text>
              <Text style={[ps.headerCell, { width: '20%', textAlign: 'center' }]}>Number of Spectators</Text>
            </View>
            {liveRows.map((w, i) => (
              <View
                style={ps.dataRow}
                key={i}
              >
                <View style={{ width: '25%' }}>
                  <View style={ps.iconRow}>
                    {w.icon && (
                      <Image
                        src={iconUrl(`/images/icons/${w.name?.toLowerCase()}.png`)}
                        style={ps.icon}
                      />
                    )}
                    <View>
                      <Text style={ps.cellBold}>{w.name}</Text>
                      {(w.name === 'Google' || w.name === 'Bing') && <Text style={ps.cellSub}>delisting</Text>}
                    </View>
                  </View>
                </View>
                <Text style={[ps.cellText, { width: '20%', textAlign: 'center' }]}>
                  {w.name === 'Google' || w.name === 'Bing'
                    ? matchdayDataTotals.totalgooglelinks > 0
                      ? matchdayDataTotals.totalgooglelinks.toLocaleString()
                      : '0'
                    : w.livelinks > 0
                    ? w.livelinks.toLocaleString()
                    : '0'}
                </Text>
                <Text style={[ps.cellText, { width: '20%', textAlign: 'center' }]}>
                  {w.time !== null ? w.time : ''}
                </Text>
                <Text style={[ps.cellText, { width: '15%', textAlign: 'center' }]}>
                  {w.rate !== null ? w.rate + '%' : ''}
                </Text>
                <Text style={[ps.cellText, { width: '20%', textAlign: 'center' }]}>
                  {w.totalspectators === 0 || w.totalspectators === null ? '-' : fmtNum(w.totalspectators)}
                </Text>
              </View>
            ))}
            <View style={ps.totalsRow}>
              <Text style={[ps.totalsText, { width: '25%' }]}>TOTAL</Text>
              <Text style={[ps.totalsText, { width: '20%', textAlign: 'center' }]}>
                {liveTotalLinks > 0 ? liveTotalLinks.toLocaleString() : '-'}{' '}
                LINKS
              </Text>
              <Text style={{ width: '20%' }}></Text>
              <Text style={{ width: '15%' }}></Text>
              <Text style={[ps.totalsText, { width: '20%', textAlign: 'center' }]}>
                {matchdayDataSpectators > 0 ? `${fmtNum(matchdayDataSpectators)} SPECTATORS` : '- SPECTATORS'}
              </Text>
            </View>
          </View>
        </View>

        {/* ── PIRACY HIGHLIGHTS ────────────────────────────────── */}
        <View
          style={ps.spacer}
          break
        >
          <View style={ps.sectionBox}>
            <Text style={ps.sectionTitle}>PIRACY HIGHLIGHTS</Text>
          </View>
          <View style={ps.tableWrap}>
            <Text style={ps.tableTitle}>Summary table for highlights links</Text>
            <View style={ps.headerRow}>
              <Text style={[ps.headerCell, { width: '30%', textAlign: 'left' }]}>Platforms</Text>
              <Text style={[ps.headerCell, { width: '30%', textAlign: 'center' }]}>
                Number of notified highlights links
              </Text>
              <Text style={[ps.headerCell, { width: '20%', textAlign: 'center' }]}>Average closure time</Text>
              <Text style={[ps.headerCell, { width: '20%', textAlign: 'center' }]}>Closure rate</Text>
            </View>
            {highlightRows.map((w, i) => (
              <View
                style={ps.dataRow}
                key={i}
              >
                <View style={{ width: '30%' }}>
                  <View style={ps.iconRow}>
                    {w.icon && (
                      <Image
                        src={iconUrl(`/images/icons/${w.name?.toLowerCase()}.png`)}
                        style={ps.icon}
                      />
                    )}
                    <View>
                      <Text style={ps.cellBold}>{w.name}</Text>
                      {(w.name === 'Google' || w.name === 'Bing') && <Text style={ps.cellSub}>delisting</Text>}
                    </View>
                  </View>
                </View>
                <Text style={[ps.cellText, { width: '30%', textAlign: 'center' }]}>
                  {w.name === 'Google' || w.name === 'Bing'
                    ? matchdayDataTotals.totalhighlightlinks - highlightWebsitesTotal > 0
                      ? (matchdayDataTotals.totalhighlightlinks - highlightWebsitesTotal).toLocaleString()
                      : '0'
                    : w.highlightlinks > 0
                    ? w.highlightlinks.toLocaleString()
                    : '0'}
                </Text>
                <Text style={[ps.cellText, { width: '20%', textAlign: 'center' }]}>
                  {w.highlighttime !== null ? w.highlighttime : ''}
                </Text>
                <Text style={[ps.cellText, { width: '20%', textAlign: 'center' }]}>
                  {w.highlightrate !== null ? w.highlightrate + '%' : ''}
                </Text>
              </View>
            ))}
            <View style={ps.totalsRow}>
              <Text style={[ps.totalsText, { width: '30%' }]}>TOTAL</Text>
              <Text style={[ps.totalsText, { width: '30%', textAlign: 'center' }]}>
                {highlightTotalLinks > 0 ? highlightTotalLinks.toLocaleString() : '-'}{' '}
                LINKS
              </Text>
              <Text style={{ width: '20%' }}></Text>
              <Text style={{ width: '20%' }}></Text>
            </View>
          </View>
        </View>

        {/* ── TOP EVENTS – TOTAL ───────────────────────────────── */}
        <View
          style={ps.spacer}
          break
        >
          <View style={ps.sectionBox}>
            <Text style={ps.sectionSubtitle}>TOTAL</Text>
            <Text style={ps.sectionTitle}>TOP EVENTS</Text>
          </View>
          <View style={ps.tableWrap}>
            <View style={ps.whiteHeaderRow}>
              <Text style={[ps.whiteHeaderCell, { width: '45%', textAlign: 'left' }]}>Event</Text>
              <Text style={[ps.whiteHeaderCell, { width: '25%', textAlign: 'center' }]}>Date</Text>
              <Text style={[ps.whiteHeaderCell, { width: '30%', textAlign: 'center' }]}>Total of links</Text>
            </View>
            <View style={ps.subHeaderRow}>
              <Text style={[ps.subHeaderCell, { width: '100%' }]}>Total (Live, Highlights, Google)</Text>
            </View>
            {matchTotal.map((m, i) => (
              <View
                style={ps.dataRow}
                key={i}
              >
                <Text style={[ps.cellBold, { width: '45%' }]}>
                  {m.team1name} - {m.team2name}
                </Text>
                <Text style={[ps.cellText, { width: '25%', textAlign: 'center' }]}>{format(m.date, 'dd/MM/yyyy')}</Text>
                <Text style={[ps.cellText, { width: '30%', textAlign: 'center' }]}>
                  {m.totallinks > 0 ? Number(m.totallinks).toLocaleString() : ''}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── TOP EVENTS – LIVE ────────────────────────────────── */}
        <View
          style={ps.spacer}
          break
        >
          <View style={ps.sectionBox}>
            <Text style={ps.sectionSubtitle}>LIVE</Text>
            <Text style={ps.sectionTitle}>TOP EVENTS</Text>
          </View>
          <View style={ps.tableWrap}>
            <View style={ps.whiteHeaderRow}>
              <Text style={[ps.whiteHeaderCell, { width: '45%', textAlign: 'left' }]}>Event</Text>
              <Text style={[ps.whiteHeaderCell, { width: '25%', textAlign: 'center' }]}>Date</Text>
              <Text style={[ps.whiteHeaderCell, { width: '30%', textAlign: 'center' }]}>Total of links</Text>
            </View>
            <View style={ps.subHeaderRow}>
              <Text style={[ps.subHeaderCell, { width: '100%' }]}>Live</Text>
            </View>
            {matchLive.map((m, i) => (
              <View
                style={ps.dataRow}
                key={i}
              >
                <Text style={[ps.cellBold, { width: '45%' }]}>
                  {m.team1name} - {m.team2name}
                </Text>
                <Text style={[ps.cellText, { width: '25%', textAlign: 'center' }]}>{format(m.date, 'dd/MM/yyyy')}</Text>
                <Text style={[ps.cellText, { width: '30%', textAlign: 'center' }]}>
                  {m.livelinks > 0 ? m.livelinks.toLocaleString() : ''}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── TOP EVENTS – SPECTATORS ──────────────────────────── */}
        <View
          style={ps.spacer}
          break
        >
          <View style={ps.sectionBox}>
            <Text style={ps.sectionSubtitle}>SPECTATORS</Text>
            <Text style={ps.sectionTitle}>TOP EVENTS</Text>
          </View>
          <View style={ps.tableWrap}>
            <View style={ps.whiteHeaderRow}>
              <Text style={[ps.whiteHeaderCell, { width: '45%', textAlign: 'left' }]}>Event</Text>
              <Text style={[ps.whiteHeaderCell, { width: '25%', textAlign: 'center' }]}>Date</Text>
              <Text style={[ps.whiteHeaderCell, { width: '30%', textAlign: 'center' }]}>NUMBER OF SPECTATORS</Text>
            </View>
            <View style={ps.subHeaderRow}>
              <Text style={[ps.subHeaderCell, { width: '100%' }]}>Spectators</Text>
            </View>
            {matchSpec.map((m, i) => (
              <View
                style={ps.dataRow}
                key={i}
              >
                <Text style={[ps.cellBold, { width: '45%' }]}>
                  {m.team1name} - {m.team2name}
                </Text>
                <Text style={[ps.cellText, { width: '25%', textAlign: 'center' }]}>{format(m.date, 'dd/MM/yyyy')}</Text>
                <Text style={[ps.cellText, { width: '30%', textAlign: 'center' }]}>
                  {m.totalspectators > 0 ? Number(m.totalspectators).toLocaleString() : ''}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── TOP EVENTS – HIGHLIGHTS ──────────────────────────── */}
        <View
          style={ps.spacer}
          break
        >
          <View style={ps.sectionBox}>
            <Text style={ps.sectionSubtitle}>HIGHLIGHTS</Text>
            <Text style={ps.sectionTitle}>TOP EVENTS</Text>
          </View>
          <View style={ps.tableWrap}>
            <View style={ps.whiteHeaderRow}>
              <Text style={[ps.whiteHeaderCell, { width: '45%', textAlign: 'left' }]}>Event</Text>
              <Text style={[ps.whiteHeaderCell, { width: '25%', textAlign: 'center' }]}>Date</Text>
              <Text style={[ps.whiteHeaderCell, { width: '30%', textAlign: 'center' }]}>Total of links</Text>
            </View>
            <View style={ps.subHeaderRow}>
              <Text style={[ps.subHeaderCell, { width: '100%' }]}>Highlights</Text>
            </View>
            {matchHL.map((m, i) => (
              <View
                style={ps.dataRow}
                key={i}
              >
                <Text style={[ps.cellBold, { width: '45%' }]}>
                  {m.team1name} - {m.team2name}
                </Text>
                <Text style={[ps.cellText, { width: '25%', textAlign: 'center' }]}>{format(m.date, 'dd/MM/yyyy')}</Text>
                <Text style={[ps.cellText, { width: '30%', textAlign: 'center' }]}>
                  {m.highlightlinks > 0 ? m.highlightlinks.toLocaleString() : ''}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── NOTIFIED AND SUSPENDED ACCOUNTS ──────────────────── */}
        <View
          style={ps.spacer}
          break
        >
          <View style={ps.sectionBox}>
            <Text style={ps.sectionTitle}>NOTIFIED AND SUSPENDED ACCOUNTS</Text>
            <View style={ps.socialRow}>
              <Text style={ps.socialItem}>X / Twitter</Text>
              <Text style={ps.socialItem}>Tiktok</Text>
              <Text style={ps.socialItem}>Telegram</Text>
              <Text style={ps.socialItem}>Discord</Text>
              <Text style={ps.socialItem}>Snapchat</Text>
            </View>
          </View>

          {/* Suspended */}
          <View style={ps.accCard}>
            <View style={ps.accHeader}>
              <Text style={ps.accHeaderText}>Suspended Accounts</Text>
            </View>
            <View style={ps.accRow}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/x.png')}
                  style={ps.icon}
                />
                <Text style={ps.cellBold}>X / Twitter</Text>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.accsuspendedtwitter > 0 ? matchdayData.accsuspendedtwitter.toLocaleString() : ''}
              </Text>
            </View>
            <View style={ps.accRow}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/tiktok.png')}
                  style={ps.icon}
                />
                <Text style={ps.cellBold}>Tiktok</Text>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.accsuspendedtiktok > 0 ? matchdayData.accsuspendedtiktok.toLocaleString() : ''}
              </Text>
            </View>
            <View style={ps.accRow}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/telegram.png')}
                  style={ps.icon}
                />
                <View>
                  <Text style={ps.cellBold}>Telegram</Text>
                  <Text style={ps.cellSub}>Suspended channels</Text>
                </View>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.accsuspendedtelegram > 0 ? matchdayData.accsuspendedtelegram.toLocaleString() : ''}
              </Text>
            </View>
            <View style={ps.accRowLast}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/discord.png')}
                  style={ps.icon}
                />
                <View>
                  <Text style={ps.cellBold}>Discord</Text>
                  <Text style={ps.cellSub}>Suspended servers</Text>
                </View>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.accsuspendeddiscord > 0 ? matchdayData.accsuspendeddiscord.toLocaleString() : ''}
              </Text>
            </View>
          </View>

          {/* Notified */}
          <View style={[ps.accCard, { marginTop: 8 }]}>
            <View style={ps.accHeader}>
              <Text style={ps.accHeaderText}>Notified Accounts</Text>
            </View>
            <View style={ps.accRow}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/x.png')}
                  style={ps.icon}
                />
                <Text style={ps.cellBold}>X / Twitter</Text>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.accnotifiedtwitter > 0 ? matchdayData.accnotifiedtwitter.toLocaleString() : ''}
              </Text>
            </View>
            <View style={ps.accRow}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/tiktok.png')}
                  style={ps.icon}
                />
                <Text style={ps.cellBold}>Tiktok</Text>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.accnotifiedtiktok > 0 ? matchdayData.accnotifiedtiktok.toLocaleString() : ''}
              </Text>
            </View>
            <View style={ps.accRowLast}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/snapchat.png')}
                  style={ps.icon}
                />
                <Text style={ps.cellBold}>Snapchat</Text>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.accnotifiedsnapchat > 0 ? matchdayData.accnotifiedsnapchat.toLocaleString() : ''}
              </Text>
            </View>
          </View>

          {/* Channels Notified */}
          <View style={[ps.accCard, { marginTop: 8 }]}>
            <View style={ps.accHeader}>
              <Text style={ps.accHeaderText}>Channels Notified</Text>
            </View>
            <View style={ps.accRowLast}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/telegram.png')}
                  style={ps.icon}
                />
                <Text style={ps.cellBold}>Telegram</Text>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.accnotifiedtelegram > 0 ? matchdayData.accnotifiedtelegram.toLocaleString() : ''}
              </Text>
            </View>
          </View>

          {/* Impacted Members */}
          <View style={[ps.accCard, { marginTop: 8 }]}>
            <View style={ps.accHeader}>
              <Text style={ps.accHeaderText}>Impacted Members</Text>
            </View>
            <View style={ps.accRowLast}>
              <View style={[ps.iconRow, { width: '50%' }]}>
                <Image
                  src={iconUrl('/images/icons/telegram.png')}
                  style={ps.icon}
                />
                <Text style={ps.cellBold}>Telegram</Text>
              </View>
              <Text style={[ps.cellText, { width: '50%', textAlign: 'center' }]}>
                {matchdayData?.membersinspactedtelegram > 0
                  ? matchdayData.membersinspactedtelegram.toLocaleString()
                  : ''}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// DIALOG COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const ReportPdfDialog = ({ isOpen, onClose, data }) => {
  if (!data) return null

  const handleDownload = (blob) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Report-Day${data.matchday}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handlePrint = (url) => {
    const printWindow = window.open(url)
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print()
      })
    }
  }

  return (
    <Transition
      appear
      show={isOpen}
      as={Fragment}
    >
      <Dialog
        as='div'
        className='relative z-50'
        onClose={onClose}
      >
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black/60' />
        </Transition.Child>

        {/* Dialog content */}
        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='w-full max-w-5xl transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all'>
                {/* Header */}
                <div className='flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50'>
                  <Dialog.Title className='text-lg font-semibold text-gray-900'>
                    Report Preview — Day {data.matchday}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className='rounded-lg p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors'
                  >
                    <IoClose size={22} />
                  </button>
                </div>

                {/* Body */}
                <div
                  className='p-4'
                  style={{ height: '75vh' }}
                >
                  <BlobProvider document={<ReportPdfDocument {...data} />}>
                    {({ blob, url, loading, error }) => {
                      if (loading) {
                        return (
                          <div className='flex flex-col items-center justify-center h-full space-y-4'>
                            <Spinner
                              color='#5c2d91'
                              size={32}
                            />
                            <p className='text-gray-500 text-sm'>Generating PDF...</p>
                          </div>
                        )
                      }

                      if (error) {
                        return (
                          <div className='flex flex-col items-center justify-center h-full space-y-3'>
                            <p className='text-red-500 font-medium'>Failed to generate PDF</p>
                            <p className='text-gray-400 text-sm'>{error.message}</p>
                          </div>
                        )
                      }

                      return (
                        <div className='flex flex-col h-full'>
                          {/* Action buttons */}
                          <div className='flex justify-end gap-2 mb-3'>
                            <button
                              onClick={() => handlePrint(url)}
                              className='btn-main inline-flex items-center gap-2 px-4 py-2'
                              style={{ alignItems: 'center' }}
                            >
                              <FiPrinter size={16} />
                              <span>Print</span>
                            </button>
                            <button
                              onClick={() => {
                                handleDownload(blob)
                                onClose()
                              }}
                              className='btn-main inline-flex items-center gap-2 px-4 py-2'
                              style={{ alignItems: 'center' }}
                            >
                              <GrDownload size={14} />
                              <span>Download</span>
                            </button>
                          </div>

                          {/* PDF Preview */}
                          <iframe
                            src={url}
                            title='PDF Preview'
                            className='w-full flex-1 rounded-lg border border-gray-200'
                            allow
                          />
                        </div>
                      )
                    }}
                  </BlobProvider>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ReportPdfDialog
